import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, ArrowRight, ArrowLeft, X, Upload, Image, Globe } from '@phosphor-icons/react';
import { useAccount, useContractWrite, usePublicClient, useNetwork, useBalance, useWalletClient, useContractRead } from 'wagmi';
import { ADDRESSES, CONFIG, TOKEN_NAMES, TOKEN_SYMBOLS, TOKEN_ICON_SYMBOLS } from '../config';
import { Abi, parseEther, Address, isAddress, formatEther } from 'viem';
import { decodeEventLog } from 'viem';
import { toast } from 'react-toastify';
import { formatTokenAmount } from '../utils/formatting';
import { uploadFileToBackend, uploadDirectoryToBackend, checkBackendHealth } from '../utils/backendUpload';

// Utility function to format numbers with commas
interface BondWizardProps {
  entityAddress: Address;
  onClose: () => void;
  onSuccess?: (productAddress: `0x${string}`) => void;
}

interface DeploymentDetails {
  hash: string;
  bondAddress?: `0x${string}`;
  status: 'pending' | 'complete' | 'error';
  error?: string;
}

interface CouponTokenDetails {
  hash: string;
  tokenAddress?: `0x${string}`;
  status: 'pending' | 'complete' | 'error';
  error?: string;
}

interface UIGenerationDetails {
  status: 'pending' | 'complete' | 'error';
  error?: string;
  imageUrl?: string;
  uiPageUrl?: string;
}

export function BondWizard({ entityAddress, onClose, onSuccess }: BondWizardProps) {
  // Step management
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedAssets, setSelectedAssets] = useState<Set<Address>>(new Set());
  const [assetAmounts, setAssetAmounts] = useState<Record<string, string>>({});
  const [approvalStatus, setApprovalStatus] = useState<Record<string, boolean>>({});
  
  // Coupon token creation
  const [couponName, setCouponName] = useState('');
  const [couponSymbol, setCouponSymbol] = useState('');
  const [createCouponToken, setCreateCouponToken] = useState(false);
  const [couponTokenDetails, setCouponTokenDetails] = useState<CouponTokenDetails | null>(null);
  
  // UI Generation
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isGeneratingUI, setIsGeneratingUI] = useState(false);
  const [uiGenerationDetails, setUiGenerationDetails] = useState<UIGenerationDetails | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Transaction states
  const [isDeploying, setIsDeploying] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isFunding, setIsFunding] = useState(false);
  const [isCreatingCoupon, setIsCreatingCoupon] = useState(false);
  const [deploymentDetails, setDeploymentDetails] = useState<DeploymentDetails | null>(null);
  
  // Wagmi hooks
  const { address } = useAccount();
  const { chain } = useNetwork();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();

  // Get entity token address
  const { data: entityTokenAddress } = useContractRead({
    address: entityAddress,
    abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
    functionName: 'entityToken',
  });

  // Available assets
  const availableAssets = [
    { address: ADDRESSES.FED as Address, name: 'FED' },
    { address: ADDRESSES.TRINITY_TOKEN as Address, name: 'Trinity Token' },
    { address: ADDRESSES.TBILL as Address, name: 'TBILL' },
    { address: ADDRESSES.PDAI as Address, name: 'pDAI' },
  ];

  // Get balances for all assets
  const assetBalances = availableAssets.map(asset => {
    const { data: balance } = useBalance({
      address,
      token: asset.address,
      watch: true,
    });
    return { ...asset, balance };
  });

  // Step 1: Deploy Bond Contract
  const deployProductArgs = useMemo(() => [
    'TrinityBond',
    ADDRESSES.COUPON_TOKEN as Address,
    '0x0000000000000000000000000000000000000000' as Address,
    name,
    description
  ], [address, name, description]);

  const { write: deployProduct } = useContractWrite({
    address: entityAddress,
    abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
    functionName: 'deployProduct',
    args: deployProductArgs,
    onSuccess: async (tx) => {
      setDeploymentDetails({
        hash: tx.hash,
        status: 'pending'
      });
      
      try {
        const receipt = await publicClient.waitForTransactionReceipt({ hash: tx.hash });
        const productDeployedEvent = receipt.logs.find(log => {
          try {
            if (log.address.toLowerCase() !== entityAddress.toLowerCase()) return false;
            const event = decodeEventLog({
              abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
              data: log.data,
              topics: log.topics
            });
            return event.eventName === 'ProductDeployed';
          } catch {
            return false;
          }
        });

        if (!productDeployedEvent) throw new Error('No ProductDeployed event found');

        const event = decodeEventLog({
          abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
          data: productDeployedEvent.data,
          topics: productDeployedEvent.topics
        }) as unknown as { eventName: 'ProductDeployed'; args: { product: Address } };

        setDeploymentDetails({
          hash: tx.hash,
          bondAddress: event.args.product,
          status: 'complete'
        });
        
        // Move to next step
        setStep(3);
        setIsDeploying(false);
        toast.success('Bond created successfully!');
      } catch (error) {
        setDeploymentDetails(prev => ({
          ...prev!,
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error'
        }));
        setIsDeploying(false);
        toast.error('Failed to create bond');
      }
    }
  });

  // Step 2: Create Coupon Token
  const { write: createCouponTokenWrite } = useContractWrite({
    address: ADDRESSES.NT as Address,
    abi: CONFIG.NT_ABI as Abi,
    functionName: 'NewCP',
    args: [couponName, couponSymbol, 0n, entityTokenAddress as Address],
    onSuccess: async (tx) => {
      setCouponTokenDetails({
        hash: tx.hash,
        status: 'pending'
      });
      
      try {
        const receipt = await publicClient.waitForTransactionReceipt({ hash: tx.hash });
        const tokenCreatedEvent = receipt.logs.find(log => {
          try {
            if (log.address.toLowerCase() !== ADDRESSES.NT.toLowerCase()) return false;
            const event = decodeEventLog({
              abi: CONFIG.NT_ABI as Abi,
              data: log.data,
              topics: log.topics
            });
            return event.eventName === 'TokenCreated';
          } catch {
            return false;
          }
        });

        if (!tokenCreatedEvent) throw new Error('No TokenCreated event found');

        const event = decodeEventLog({
          abi: CONFIG.NT_ABI as Abi,
          data: tokenCreatedEvent.data,
          topics: tokenCreatedEvent.topics
        }) as unknown as { eventName: 'TokenCreated'; args: { token: Address } };

        setCouponTokenDetails({
          hash: tx.hash,
          tokenAddress: event.args.token,
          status: 'complete'
        });
        
        // Move to next step
        setStep(2);
        setIsCreatingCoupon(false);
        toast.success('Coupon token created successfully!');
      } catch (error) {
        setCouponTokenDetails(prev => ({
          ...prev!,
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error'
        }));
        setIsCreatingCoupon(false);
        toast.error('Failed to create coupon token');
      }
    }
  });

  // Step 4: Fund Bond
  const handleSubmit = async () => {
    if (!deploymentDetails?.bondAddress || !walletClient) return;
    
    const assetsArray = Array.from(selectedAssets);
    const depositsArray = assetsArray.map(asset => parseEther(assetAmounts[asset] || '0'));
    
    setIsFunding(true);
    try {
      // Check and handle approvals first
      for (const asset of assetsArray) {
        if (!approvalStatus[asset]) {
          setIsApproving(true);
          // First approve
          const { request: approveRequest } = await publicClient.simulateContract({
            address: asset,
            abi: CONFIG.ERC20_ABI.abi as Abi,
            functionName: 'approve',
            args: [deploymentDetails.bondAddress, parseEther(assetAmounts[asset] || '0')]
          });

          const approveHash = await walletClient.writeContract(approveRequest);
          await publicClient.waitForTransactionReceipt({ hash: approveHash });
          
          setApprovalStatus(prev => ({
            ...prev,
            [asset]: true
          }));
          toast.success(`Approved ${TOKEN_SYMBOLS[asset as keyof typeof TOKEN_SYMBOLS]}`);
        }
      }
      setIsApproving(false);

      // Then call addAssetsAndDeposit
      const { request: depositRequest } = await publicClient.simulateContract({
        address: deploymentDetails.bondAddress,
        abi: CONFIG.TRINITY_BOND_ABI.abi as Abi,
        functionName: 'addAssetsAndDeposit',
        args: [assetsArray, depositsArray]
      });

      const depositHash = await walletClient.writeContract(depositRequest);
      await publicClient.waitForTransactionReceipt({ hash: depositHash });

      // Clear amounts
      setAssetAmounts({});
      setIsFunding(false);

      // Move to UI generation step
      setStep(5);
      
      toast.success('Bond funded successfully! Now let\'s generate the UI.');
    } catch (error) {
      console.error('Error funding:', error);
      toast.error('Failed to fund bond');
      setIsFunding(false);
      setIsApproving(false);
    }
  };

  // Step 5: Generate UI
  const handleGenerateUI = async () => {
    if (!deploymentDetails?.bondAddress || !walletClient) return;
    
    setIsGeneratingUI(true);
    setUiGenerationDetails({ status: 'pending' });
    
    try {
      // Get properties contract address from bond contract
      const propertiesAddress = await publicClient.readContract({
        address: deploymentDetails.bondAddress,
        abi: CONFIG.TRINITY_BOND_ABI.abi as Abi,
        functionName: 'properties',
        args: []
      }) as `0x${string}`;
      
      console.log('Properties contract address:', propertiesAddress);
      
      // 1. Upload image if selected
      let imageUrl: string | undefined;
      if (selectedImage) {
        const imageResult = await uploadFileToBackend(selectedImage, selectedImage.name);
        
        // Use IPFS URL with filename
        imageUrl = `https://${imageResult.cid}.ipfs.w3s.link/${selectedImage.name}`;
        toast.success('Image uploaded successfully!');
      }

      // 2. Generate and upload UI templates
      console.log('=== DEBUG: Generating UI Templates ===');
      console.log('Template data:', {
        bondName: name,
        bondDescription: description,
        bondAddress: deploymentDetails.bondAddress,
        imageUrl: imageUrl || '',
        supportedAssets: Array.from(selectedAssets)
      });
      
      // Generate product page template
      const productTemplate = generateProductPageTemplate({
        bondName: name,
        bondDescription: description,
        bondAddress: deploymentDetails.bondAddress,
        imageUrl: imageUrl || '',
        supportedAssets: Array.from(selectedAssets)
      });
      
      // Generate bond template
      const bondTemplate = generateBondUITemplate({
        bondName: name,
        bondDescription: description,
        bondAddress: deploymentDetails.bondAddress,
        imageUrl: imageUrl || '',
        supportedAssets: Array.from(selectedAssets)
      });

      console.log('Generated product template files:', Object.keys(productTemplate));
      console.log('Generated bond template files:', Object.keys(bondTemplate));

      console.log('=== DEBUG: Checking Backend Health ===');
      const backendHealth = await checkBackendHealth();
      if (!backendHealth) {
        throw new Error('Backend is not accessible. Please ensure the backend server is running.');
      }
      console.log('Backend health check passed:', backendHealth);

      console.log('=== DEBUG: Uploading Templates to Backend ===');
      let productResult, bondResult;
      try {
        // Upload both templates with retry logic
        const maxRetries = 3;
        let lastError: Error | null = null;
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            console.log(`Upload attempt ${attempt}/${maxRetries}`);
            
            // Upload both templates
            productResult = await uploadDirectoryToBackend(productTemplate);
            bondResult = await uploadDirectoryToBackend(bondTemplate);
            
            console.log('Product upload result:', productResult);
            console.log('Bond upload result:', bondResult);
            
            if (!productResult?.cid || !bondResult?.cid) {
              throw new Error('Upload failed - no CID returned');
            }
            
            // If we get here, upload was successful
            break;
            
          } catch (uploadError) {
            lastError = uploadError instanceof Error ? uploadError : new Error('Unknown upload error');
            console.error(`Upload attempt ${attempt} failed:`, lastError);
            
            if (attempt < maxRetries) {
              // Wait before retrying (exponential backoff)
              const delay = Math.pow(2, attempt) * 1000; // 2s, 4s, 8s
              console.log(`Waiting ${delay}ms before retry...`);
              await new Promise(resolve => setTimeout(resolve, delay));
            }
          }
        }
        
        // If we get here and still have an error, all retries failed
        if (lastError) {
          throw lastError;
        }
        
      } catch (uploadError) {
        console.error('Template upload error after all retries:', uploadError);
        
        // Provide more specific error messages
        let errorMessage = 'Template upload failed';
        if (uploadError instanceof Error) {
          if (uploadError.message.includes('500')) {
            errorMessage = 'Web3.Storage service is experiencing issues. Please try again later.';
          } else if (uploadError.message.includes('space/blob/add')) {
            errorMessage = 'IPFS storage allocation failed. The service may be temporarily unavailable.';
          } else if (uploadError.message.includes('Cannot read properties of undefined')) {
            errorMessage = 'Web3.Storage service configuration error. Please contact support.';
          } else {
            errorMessage = `Upload failed: ${uploadError.message}`;
          }
        }
        
        throw new Error(errorMessage);
      }
      
      // 3. Set both UI page and image in a single contract call using setUI
      // Element 1: Product page (index 0)
      // Element 2: Bond template (index 1)
      const pages = [productResult!.cid, bondResult!.cid];
      const images = imageUrl ? [imageUrl] : []; // Image at index 0 if exists
      
      console.log('Setting UI with pages:', pages, 'and images:', images);
      
      const { request: setUIRequest } = await publicClient.simulateContract({
        address: propertiesAddress,
        abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
        functionName: 'setUI',
        args: [pages, images]
      });
      
      const setUIHash = await walletClient.writeContract(setUIRequest);
      await publicClient.waitForTransactionReceipt({ hash: setUIHash });

      setUiGenerationDetails({
        status: 'complete',
        imageUrl,
        uiPageUrl: `https://${productResult!.cid}.ipfs.w3s.link/index.html`
      });
      
      setIsGeneratingUI(false);
      toast.success('Bond template uploaded successfully!');
      
      // Call onSuccess
      if (onSuccess) {
        onSuccess(deploymentDetails.bondAddress);
      }
      
    } catch (error) {
      console.error('Error generating UI:', error);
      setUiGenerationDetails({
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      setIsGeneratingUI(false);
      toast.error('Failed to generate UI');
    }
  };

  // Image handling
  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          setImagePreview(result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          setImagePreview(result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeploy = () => {
    setIsDeploying(true);
    deployProduct?.();
  };

  const handleCreateCouponToken = () => {
    if (!couponName || !couponSymbol || !entityTokenAddress) return;
    
    // Debug logging
    console.log('=== DEBUG: Create Coupon Token Parameters ===');
    console.log('ADDRESSES.NT:', ADDRESSES.NT);
    console.log('CONFIG.NT_ABI:', CONFIG.NT_ABI);
    console.log('couponName:', couponName);
    console.log('couponSymbol:', couponSymbol);
    console.log('entityTokenAddress:', entityTokenAddress);
    console.log('functionName:', 'NewCP');
    console.log('args:', [couponName, couponSymbol, 0n, entityTokenAddress as Address]);
    console.log('==========================================');
    
    setIsCreatingCoupon(true);
    createCouponTokenWrite?.();
  };

  const toggleAsset = (assetAddress: Address) => {
    const newSelectedAssets = new Set(selectedAssets);
    if (newSelectedAssets.has(assetAddress)) {
      newSelectedAssets.delete(assetAddress);
      // Clear amount when asset is removed
      const newAmounts = { ...assetAmounts };
      delete newAmounts[assetAddress];
      setAssetAmounts(newAmounts);
    } else {
      newSelectedAssets.add(assetAddress);
    }
    setSelectedAssets(newSelectedAssets);
  };

  return (
    <div className="fixed inset-0 z-50">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute top-[30vh] left-1/2 -translate-x-1/2 w-full max-w-4xl">
        <div className="bg-gray-900 rounded-lg shadow-xl p-6">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Progress Bar */}
          <div className="mb-6 p-6">
            <div className="flex justify-between mb-2">
              <span className={`text-sm ${step >= 1 ? 'text-blue-500' : 'text-gray-500'}`}>Create Coupon</span>
              <span className={`text-sm ${step >= 2 ? 'text-blue-500' : 'text-gray-500'}`}>Create Bond</span>
              <span className={`text-sm ${step >= 3 ? 'text-blue-500' : 'text-gray-500'}`}>Select Assets</span>
              <span className={`text-sm ${step >= 4 ? 'text-blue-500' : 'text-gray-500'}`}>Fund Bond</span>
              <span className={`text-sm ${step >= 5 ? 'text-blue-500' : 'text-gray-500'}`}>Upload Template</span>
            </div>
            <div className="h-2 bg-gray-700 rounded-full">
              <div 
                className="h-full bg-blue-500 rounded-full transition-all"
                style={{ width: `${(step / 5) * 100}%` }}
              />
            </div>
          </div>

          {/* Step 1: Create Coupon Token */}
          {step === 1 && (
            <div className="space-y-4 p-6">
              <div>
                <label className="block text-sm font-medium mb-1">Coupon Token Name</label>
                <input
                  type="text"
                  value={couponName}
                  onChange={(e) => setCouponName(e.target.value)}
                  className="w-full px-4 py-2 bg-black/40 rounded-lg text-white"
                  placeholder="Enter coupon token name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Coupon Token Symbol</label>
                <input
                  type="text"
                  value={couponSymbol}
                  onChange={(e) => setCouponSymbol(e.target.value)}
                  className="w-full px-4 py-2 bg-black/40 rounded-lg text-white"
                  placeholder="Enter coupon token symbol"
                />
              </div>
              <div className="text-sm text-gray-400">
                Parent Token: {entityTokenAddress ? (TOKEN_NAMES[entityTokenAddress as keyof typeof TOKEN_NAMES] || entityTokenAddress) : 'Loading...'}
              </div>
              {couponTokenDetails?.status === 'error' && (
                <div className="text-red-400 text-sm">{couponTokenDetails.error}</div>
              )}
              <div className="flex justify-end">
                <button
                  onClick={handleCreateCouponToken}
                  disabled={isCreatingCoupon || !couponName || !couponSymbol || !entityTokenAddress}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreatingCoupon ? 'Creating...' : 'Create Coupon Token'}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Deploy Bond Contract */}
          {step === 2 && (
            <div className="space-y-4 p-6">
              <div>
                <label className="block text-sm font-medium mb-1">Bond Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2 bg-black/40 rounded-lg text-white"
                  placeholder="Enter bond name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-4 py-2 bg-black/40 rounded-lg text-white h-24"
                  placeholder="Enter bond description"
                />
              </div>
              {deploymentDetails?.status === 'error' && (
                <div className="text-red-400 text-sm">{deploymentDetails.error}</div>
              )}
              <div className="flex justify-end">
                <button
                  onClick={handleDeploy}
                  disabled={isDeploying || !name || !description}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeploying ? 'Creating...' : 'Create Bond'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Select Assets */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {assetBalances.map((asset) => (
                  <motion.button
                    key={asset.address}
                    onClick={() => toggleAsset(asset.address)}
                    className={`relative overflow-hidden rounded-lg p-4 text-left transition-all ${
                      selectedAssets.has(asset.address)
                        ? 'bg-blue-500/20 border-2 border-blue-500'
                        : 'bg-gray-800 border border-gray-700 hover:bg-gray-700'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`text-3xl font-bold ${selectedAssets.has(asset.address) ? 'text-blue-400' : 'text-gray-400'}`}>
                        {TOKEN_ICON_SYMBOLS[asset.address as keyof typeof TOKEN_ICON_SYMBOLS]}
                      </div>
                      <div>
                        <div className="font-medium text-white">
                          {TOKEN_NAMES[asset.address as keyof typeof TOKEN_NAMES]}
                        </div>
                        <div className="text-sm text-gray-400">
                          {TOKEN_SYMBOLS[asset.address as keyof typeof TOKEN_SYMBOLS]}
                        </div>
                        {asset.balance && (
                          <div className="text-sm mt-1 text-gray-300">
                            Balance: {formatTokenAmount(asset.balance.value)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="absolute top-2 right-2">
                      {selectedAssets.has(asset.address) ? (
                        <Minus className="text-blue-400" size={20} />
                      ) : (
                        <Plus className="text-gray-400" size={20} />
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => setStep(4)}
                  disabled={selectedAssets.size === 0}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Fund Bond */}
          {step === 4 && (
            <div className="space-y-4">
              {Array.from(selectedAssets).map((asset) => (
                <div key={asset} className="bg-gray-800 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">
                        {TOKEN_ICON_SYMBOLS[asset as keyof typeof TOKEN_ICON_SYMBOLS]}
                      </span>
                      <span className="font-medium">
                        {TOKEN_NAMES[asset as keyof typeof TOKEN_NAMES]}
                      </span>
                    </div>
                    <div className="text-sm text-gray-400">
                      Balance: {formatTokenAmount(assetBalances.find(a => a.address === asset)?.balance?.value || 0n)}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={assetAmounts[asset] || ''}
                      onChange={(e) => setAssetAmounts(prev => ({
                        ...prev,
                        [asset]: e.target.value
                      }))}
                      className="flex-1 px-4 py-2 bg-black/40 rounded-lg text-white"
                      placeholder={`Enter amount in ${TOKEN_SYMBOLS[asset as keyof typeof TOKEN_SYMBOLS]}`}
                    />
                  </div>
                </div>
              ))}
              <div className="flex justify-end">
                <button
                  onClick={handleSubmit}
                  disabled={isFunding || isApproving || Array.from(selectedAssets).some(asset => !assetAmounts[asset])}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isApproving ? 'Approving...' : isFunding ? 'Funding...' : 'Fund Bond'}
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Upload Template */}
          {step === 5 && (
            <div className="space-y-6 p-6">
              <div className="text-center mb-6">
                <Globe className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white mb-2">Upload Bond Template</h2>
                <p className="text-gray-400">Upload an image and generate a custom UI template for your bond</p>
              </div>

              {/* Image Upload Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Image className="w-5 h-5" />
                  Bond Image
                </h3>
                
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
                    imagePreview 
                      ? 'border-blue-500 bg-blue-500/10' 
                      : 'border-gray-600 hover:border-gray-500 bg-gray-800/50'
                  }`}
                  onDrop={handleImageDrop}
                  onDragOver={handleDragOver}
                >
                  {imagePreview ? (
                    <div className="space-y-4">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-32 h-32 object-cover rounded-lg mx-auto"
                      />
                      <div className="space-y-2">
                        <p className="text-white font-medium">{selectedImage?.name}</p>
                        <button
                          onClick={removeImage}
                          className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                        >
                          Remove Image
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                      <div>
                        <p className="text-white font-medium mb-2">Drop an image here or click to browse</p>
                        <p className="text-gray-400 text-sm">Supports JPG, PNG, GIF, WebP</p>
                      </div>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                      >
                        Choose Image
                      </button>
                    </div>
                  )}
                </div>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </div>

              {/* UI Preview */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">UI Preview</h3>
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-blue-500 rounded"></div>
                      <span className="text-white font-medium">{name}</span>
                    </div>
                    <p className="text-gray-400 text-sm">{description}</p>
                    <div className="flex flex-wrap gap-2">
                      {Array.from(selectedAssets).map(asset => (
                        <span key={asset} className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300">
                          {TOKEN_SYMBOLS[asset as keyof typeof TOKEN_SYMBOLS]}
                        </span>
                      ))}
                    </div>
                    {imagePreview && (
                      <div className="mt-2">
                        <span className="text-green-400 text-xs">✓ Image will be included</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {uiGenerationDetails?.status === 'error' && (
                <div className="text-red-400 text-sm bg-red-500/10 p-3 rounded">
                  {uiGenerationDetails.error}
                </div>
              )}

              {/* Success Display */}
              {uiGenerationDetails?.status === 'complete' && (
                <div className="text-green-400 text-sm bg-green-500/10 p-3 rounded space-y-2">
                  <div>✓ UI generated successfully!</div>
                  {uiGenerationDetails.uiPageUrl && (
                    <div>
                      <span className="text-gray-400">UI Page: </span>
                      <a 
                        href={uiGenerationDetails.uiPageUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:underline"
                      >
                        View Page
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between">
                <button
                  onClick={() => setStep(4)}
                  className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleGenerateUI}
                  disabled={isGeneratingUI}
                  className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isGeneratingUI ? 'Uploading...' : 'Upload Template'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper function to generate product page template
function generateProductPageTemplate(data: {
  bondName: string;
  bondDescription: string;
  bondAddress: string;
  imageUrl: string;
  supportedAssets: Address[];
}): { [filename: string]: string } {
  const { bondName, bondDescription, bondAddress, imageUrl, supportedAssets } = data;
  
  const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${bondName} - Product Page</title>
    <link rel="stylesheet" href="./style.css">
    <script src="./lib/web3.min.js"></script>
</head>
<body>
    <div class="container">
        <header class="header">
            <div class="product-info">
                <h1 id="product-name" class="product-title">${bondName}</h1>
                <p id="product-description" class="product-description">${bondDescription}</p>
                <div class="product-meta">
                    <span id="product-type" class="product-type">TrinityBond</span>
                    <span id="product-address" class="product-address">${bondAddress}</span>
                </div>
            </div>
            <div class="product-image-container">
                ${imageUrl ? `<img id="product-image" class="product-image" src="${imageUrl}" alt="Product Image">` : ''}
                <div id="product-icon" class="product-icon" ${imageUrl ? 'style="display: none;"' : ''}>
                    <span id="product-icon-text">${bondName}</span>
                </div>
            </div>
        </header>

        <main class="main-content">
            <div class="product-details">
                <div class="detail-section">
                    <h2>Product Information</h2>
                    <div class="detail-grid">
                        <div class="detail-item">
                            <label>Product Type:</label>
                            <span id="detail-type">TrinityBond</span>
            </div>
                        <div class="detail-item">
                            <label>Contract Address:</label>
                            <span id="detail-address">${bondAddress}</span>
                        </div>
                        <div class="detail-item">
                            <label>Supported Assets:</label>
                            <span id="detail-assets">${supportedAssets.length} assets</span>
                        </div>
                    </div>
                </div>

                <div class="product-actions">
                    <h2>Actions</h2>
                    <div id="action-buttons" class="action-buttons">
                        <button class="action-button" onclick="productPage.createBond()">Create Bond</button>
                        <button class="action-button" onclick="productPage.redeemBond()">Redeem Bond</button>
            </div>
                </div>
            </div>
        </main>
    </div>

    <script src="./script.js"></script>
</body>
</html>`;

  const css = `* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
    background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
    color: #ffffff;
    line-height: 1.6;
    min-height: 100vh;
}

.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
}

.header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 40px;
    padding: 30px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 20px;
    backdrop-filter: blur(10px);
}

.product-info {
    flex: 1;
}

.product-title {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 10px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.product-description {
    font-size: 1.1rem;
    color: #a0a0a0;
    margin-bottom: 20px;
    max-width: 600px;
}

.product-meta {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
}

.product-type {
    background: rgba(102, 126, 234, 0.2);
    color: #667eea;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 500;
}

.product-address {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 0.9rem;
    font-family: 'Courier New', monospace;
    word-break: break-all;
}

.product-image-container {
    flex-shrink: 0;
    margin-left: 30px;
}

.product-image {
    width: 120px;
    height: 120px;
    border-radius: 20px;
    object-fit: contain;
    background: rgba(255, 255, 255, 0.05);
    padding: 10px;
}

.product-icon {
    width: 120px;
    height: 120px;
    border-radius: 20px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    font-weight: 700;
    color: white;
    text-align: center;
    word-break: break-word;
    padding: 10px;
}

.main-content {
    display: grid;
    gap: 30px;
}

.product-details {
    display: grid;
    gap: 30px;
}

.detail-section {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 20px;
    padding: 30px;
    backdrop-filter: blur(10px);
}

.detail-section h2 {
    font-size: 1.5rem;
    margin-bottom: 20px;
    color: #ffffff;
    font-weight: 600;
}

.detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
}

.detail-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.detail-item label {
    font-size: 0.9rem;
    color: #a0a0a0;
    font-weight: 500;
}

.detail-item span {
    font-size: 1rem;
    color: #ffffff;
    font-weight: 500;
    word-break: break-all;
}

.product-actions {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 20px;
    padding: 30px;
    backdrop-filter: blur(10px);
}

.action-buttons {
    display: flex;
    gap: 15px;
    flex-wrap: wrap;
}

.action-button {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.action-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(102, 126, 234, 0.3);
}

.action-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
}`;

  const js = `// Product Page JavaScript
class ProductPage {
    constructor() {
        this.bondAddress = '${bondAddress}';
        this.web3 = null;
        this.bondContract = null;
        this.init();
    }

    async init() {
        try {
            // Initialize Web3
            if (typeof window.ethereum !== 'undefined') {
                this.web3 = new Web3(window.ethereum);
                await window.ethereum.request({ method: 'eth_requestAccounts' });
            } else {
                console.error('MetaMask not found');
                return;
            }

            console.log('Product page initialized for bond:', this.bondAddress);
        } catch (error) {
            console.error('Initialization error:', error);
        }
    }

    async createBond() {
        console.log('Create bond functionality would be implemented here');
        alert('Create bond functionality would be implemented here');
    }

    async redeemBond() {
        console.log('Redeem bond functionality would be implemented here');
        alert('Redeem bond functionality would be implemented here');
    }
}

// Initialize the product page
const productPage = new ProductPage();`;

  // Web3 library (minified version)
  const web3Lib = `// Web3.js minified library would be included here
// For brevity, this is a placeholder
console.log('Web3 library loaded');`;

  return {
    'index.html': html,
    'style.css': css,
    'script.js': js,
    'lib/web3.min.js': web3Lib
  };
}

// Helper function to generate bond UI template using existing trinitybond files
function generateBondUITemplate(data: {
  bondName: string;
  bondDescription: string;
  bondAddress: string;
  imageUrl: string;
  supportedAssets: Address[];
}): { [filename: string]: string } {
  const { bondName, bondDescription, bondAddress, imageUrl, supportedAssets } = data;
  
  // Import the existing template files
  const trinityBondHtml = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Trinity Bond Creation</title>
    <link rel="stylesheet" href="trinitybond.css" type="text/css">
    <!-- Web3.js Library -->
    <script src="lib/web3.min.js"></script>
    <!-- Framer Motion replacement -->
    <script src="lib/framer-motion.min.js"></script>
    <!-- Inline fallback for missing external resources -->
    <script>
        // Fallback for missing ethers.js
        if (typeof ethers === 'undefined') {
            console.warn('ethers.js not loaded, using basic Web3 functionality');
            window.ethers = {
                providers: {
                    Web3Provider: function(provider) {
                        return {
                            getSigner: function() {
                                return {
                                    getAddress: function() {
                                        return provider.eth.accounts[0];
                                    }
                                };
                            }
                        };
                    }
                },
                utils: {
                    parseEther: function(value) {
                        return web3.utils.toWei(value, 'ether');
                    },
                    formatEther: function(value) {
                        return web3.utils.fromWei(value, 'ether');
                    }
                }
            };
        }
        
        // Fallback for missing GSAP
        if (typeof gsap === 'undefined') {
            console.warn('GSAP not loaded, using basic animations');
            window.gsap = {
                to: function(element, duration, properties) {
                    if (properties.opacity !== undefined) {
                        element.style.transition = 'opacity ' + (duration || 0.3) + 's';
                        element.style.opacity = properties.opacity;
                    }
                }
            };
        }

        // Listen for PING messages from parent window
        window.addEventListener('message', function(event) {
            if (event.data && event.data.type === 'PING') {
                // Send PONG back to indicate we're ready
                window.parent.postMessage({ type: 'PONG' }, '*');
            }
        });

        // Send ready signal when page is fully loaded
        window.addEventListener('load', function() {
            // Wait a bit for any dynamic content to load
            setTimeout(function() {
                window.parent.postMessage({ type: 'PONG' }, '*');
            }, 1000);
        });
    </script>
</head>
<body>
    <div id="app" class="space-y-6">
        <!-- Contract Address Display -->
        <div class="max-w-2xl mx-auto">
            <div class="relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br from-purple-500/20 via-indigo-500/20 to-blue-500/20">
                <div class="flex items-center gap-2 mb-0">
                    <h2 class="text-2xl font-bold text-white">Buy <span id="productName">${bondName}</span></h2>
                    <button
                        class="text-white/60 hover:text-white/80 transition-colors bg-transparent"
                        onclick="trinityBondApp.showInfoModal()"
                    >
                        <svg width="20" height="20" viewBox="0 0 256 256" fill="currentColor">
                            <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm16-40a8,8,0,0,1-16,0V128a8,8,0,0,1,16,0Zm0-80a8,8,0,0,1-16,0,8,8,0,0,1,16,0Z"/>
                        </svg>
                </button>
            </div>
                <div class="text-xs text-gray-400" id="productDescription">${bondDescription}</div>

                <!-- Info Modal -->
                <div id="infoModal" class="modal" style="display: none;">
                    <div class="modal-content">
                        <h3 class="text-xl font-bold mb-4 text-white">What is a Bond?</h3>
                        <p class="text-white/80 text-base leading-relaxed">
                            Peer to Peer Lending with interest.
                        </p>
                        <button
                            class="mt-6 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition-colors text-white/80 hover:text-white"
                            onclick="trinityBondApp.hideInfoModal()"
                        >
                            Got it!
                </button>
            </div>
                </div>

                <!-- Success Modal -->
                <div id="successModal" class="modal" style="display: none;">
                    <div class="modal-content">
                        <h3 class="text-xl font-bold mb-4 text-white">Bond Purchase Successful! 🎉</h3>
                        
                        <!-- Asset and Amount -->
                        <div class="mb-4">
                            <h4 class="text-sm font-semibold text-white/60 mb-1">Asset</h4>
                            <p class="text-lg font-medium text-white" id="successAsset"></p>
                        </div>

                        <!-- Term and Future Payout -->
                        <div class="mb-4">
                            <h4 class="text-sm font-semibold text-white/60 mb-1">Term and Return</h4>
                            <p class="text-white" id="successTerm"></p>
                            <p class="text-white" id="successReturn"></p>
                            <p class="text-white" id="successPayout"></p>
                        </div>

                        <!-- Fees Paid -->
                        <div class="mb-4">
                            <h4 class="text-sm font-semibold text-white/60 mb-1">Fees Paid</h4>
                            <p class="text-white" id="successTrinityFee"></p>
                            <p class="text-white" id="successServiceFee"></p>
                        </div>

                        <!-- Future Fees -->
                        <div class="mb-4">
                            <h4 class="text-sm font-semibold text-white/60 mb-1">Future Fees</h4>
                            <p class="text-white" id="successRedemptionFee"></p>
                            <p class="text-white" id="successReclaimFee"></p>
                        </div>

                        <button
                            class="mt-4 w-full px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition-colors text-white/80 hover:text-white"
                            onclick="trinityBondApp.hideSuccessModal()"
                        >
                            Close
                        </button>
                    </div>
                </div>

                <div id="errorDisplay" class="mb-3 p-2 bg-red-500/10 border border-red-500/20 rounded-lg" style="display: none;">
                    <p class="text-red-500 dark:text-red-400 text-center font-medium" id="errorMessage"></p>
                </div>

                <form id="bondForm" class="space-y-3">
                    <!-- Asset Selection -->
                    <div>
                        <div class="mt-5"></div>
                        <div id="assetCard" class="flex flex-col items-center gap-6">
                            <!-- Asset display will be populated by JavaScript, images are in lib/ -->
                        </div>
                    </div>

                    <!-- Amount Selection -->
                    <div class="mt-10">
                        <div class="mt-5"></div>
                        <div class="flex justify-center gap-2" id="amountButtons">
                            <!-- Amount buttons will be populated by JavaScript -->
                        </div>
                        <div id="balanceInfo" class="flex flex-col" style="display: none;">
                            <div class="flex justify-between items-center">
                                <div class="text-sm text-gray-400" id="userBalance"></div>
                                <div class="text-sm text-gray-400" id="walletLimit"></div>
                            </div>
                            <div class="text-sm text-gray-400" id="contractBalance"></div>
                        </div>
                    </div>

                    <!-- Term Selection -->
                    <div>
                        <div class="mt-10"></div>
                        <div class="grid grid-cols-3 gap-2" id="termButtons">
                            <!-- Term buttons will be populated by JavaScript -->
                        </div>
                    </div>

                    <!-- Fees Display -->
                    <div class="text-xs transform origin-top-left">
                        <div class="flex flex-col gap-0.5">
                            <div class="flex justify-between items-center">
                                <span class="text-gray-400">Purchase Fee:</span>
                                <span class="text-gray-400" id="purchaseFee">0 TRNTY</span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-gray-400">Service Fee:</span>
                                <span class="text-gray-400" id="serviceFee">0%</span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-gray-400">Redemption Fee:</span>
                                <span class="text-gray-400" id="redemptionFee">0%</span>
                            </div>
                            <div class="flex justify-between items-center">
                                <span class="text-gray-400">Reclaim Fee:</span>
                                <span class="text-gray-400" id="reclaimFee">0%</span>
                            </div>
                        </div>
                    </div>

                    <!-- Buttons -->
                    <div class="space-y-2">
                        <button
                            type="button"
                            id="approveButton"
                            class="w-full scale-80 transform origin-top px-4 py-2 text-base font-semibold rounded-xl transition-all duration-200 bg-orange-500 hover:opacity-90 disabled:bg-white/5 disabled:cursor-not-allowed text-white mt-2"
                            style="display: none;"
                            onclick="trinityBondApp.handleApproveToken()"
                        >
                            Approve Token
                        </button>

                        <button
                            type="submit"
                            id="createBondButton"
                            class="w-full scale-80 transform origin-top px-4 py-2 text-base font-semibold rounded-xl transition-all duration-200 bg-blue-500 hover:opacity-90 text-white mt-2"
                            onclick="trinityBondApp.handleSubmit(event)"
                        >
                            Purchase
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>

    <script src="trinitybond.js"></script>
</body>
</html>`;

  // For now, I'll use a simplified CSS and JS that matches the trinitybond structure
  // In a real implementation, you would import the actual files
  const trinityBondCss = `/* Trinity Bond Creation Styles */
* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
    background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
    color: white;
    min-height: 100vh;
    overflow-x: hidden;
}

/* Basic utility classes */
.space-y-6 > * + * { margin-top: 1.5rem; }
.space-y-3 > * + * { margin-top: 0.75rem; }
.space-y-2 > * + * { margin-top: 0.5rem; }
.max-w-2xl { max-width: 42rem; }
.mx-auto { margin-left: auto; margin-right: auto; }
.flex { display: flex; }
.flex-col { flex-direction: column; }
.items-center { align-items: center; }
.justify-center { justify-content: center; }
.justify-between { justify-content: space-between; }
.gap-2 { gap: 0.5rem; }
.gap-4 { gap: 1rem; }
.gap-6 { gap: 1.5rem; }
.grid { display: grid; }
.grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.w-full { width: 100%; }
.hidden { display: none; }
.relative { position: relative; }
.absolute { position: absolute; }
.fixed { position: fixed; }
.inset-0 { top: 0; right: 0; bottom: 0; left: 0; }
.z-50 { z-index: 50; }

/* Spacing */
.mb-0 { margin-bottom: 0; }
.mb-1 { margin-bottom: 0.25rem; }
.mb-3 { margin-bottom: 0.75rem; }
.mb-4 { margin-bottom: 1rem; }
.mt-2 { margin-top: 0.5rem; }
.mt-5 { margin-top: 1.25rem; }
.mt-10 { margin-top: 2.5rem; }

/* Padding */
.p-2 { padding: 0.5rem; }
.p-4 { padding: 1rem; }
.px-4 { padding-left: 1rem; padding-right: 1rem; }
.py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }

/* Text */
.text-xs { font-size: 0.75rem; }
.text-sm { font-size: 0.875rem; }
.text-base { font-size: 1rem; }
.text-lg { font-size: 1.125rem; }
.text-2xl { font-size: 1.5rem; }
.text-xl { font-size: 1.25rem; }
.font-bold { font-weight: 700; }
.font-medium { font-weight: 500; }
.font-semibold { font-weight: 600; }
.text-center { text-align: center; }

/* Colors */
.text-white { color: rgb(255 255 255); }
.text-gray-400 { color: rgb(156 163 175); }
.text-red-500 { color: rgb(239 68 68); }
.text-blue-500 { color: rgb(59 130 246); }
.text-white\\/60 { color: rgb(255 255 255 / 0.6); }
.text-white\\/80 { color: rgb(255 255 255 / 0.8); }

.bg-transparent { background-color: transparent; }
.bg-blue-500 { background-color: rgb(59 130 246); }
.bg-orange-500 { background-color: rgb(249 115 22); }
.bg-red-500\\/10 { background-color: rgb(239 68 68 / 0.1); }
.bg-blue-500\\/20 { background-color: rgba(59, 130, 246, 0.2); }
.bg-blue-500\\/30 { background-color: rgba(59, 130, 246, 0.3); }
.bg-white\\/5 { background-color: rgb(255 255 255 / 0.05); }

/* Gradients */
.bg-gradient-to-br {
    background: linear-gradient(to bottom right, var(--tw-gradient-stops));
}
.from-purple-500\\/20 {
    --tw-gradient-from: rgb(168 85 247 / 0.2);
    --tw-gradient-to: rgb(168 85 247 / 0);
    --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
}
.via-indigo-500\\/20 {
    --tw-gradient-to: rgb(99 102 241 / 0);
    --tw-gradient-stops: var(--tw-gradient-from), rgb(99 102 241 / 0.2), var(--tw-gradient-to);
}
.to-blue-500\\/20 {
    --tw-gradient-to: rgb(59 130 246 / 0.2);
}

/* Borders */
.border { border-width: 1px; }
.border-red-500\\/20 { border-color: rgb(239 68 68 / 0.2); }

/* Border Radius */
.rounded-lg { border-radius: 0.5rem; }
.rounded-xl { border-radius: 0.75rem; }
.rounded-2xl { border-radius: 1rem; }

/* Transitions */
.transition-all { transition-property: all; transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1); transition-duration: 150ms; }
.transition-colors { transition-property: color, background-color, border-color, text-decoration-color, fill, stroke; transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1); transition-duration: 150ms; }

/* Hover States */
.hover\\:opacity-90:hover { opacity: 0.9; }
.hover\\:bg-blue-500\\/30:hover { background-color: rgba(59, 130, 246, 0.3); }
.hover\\:text-white\\/80:hover { color: rgb(255 255 255 / 0.8); }
.hover\\:text-white:hover { color: rgb(255 255 255); }

/* Disabled States */
.disabled\\:bg-white\\/5:disabled { background-color: rgb(255 255 255 / 0.05); }
.disabled\\:cursor-not-allowed:disabled { cursor: not-allowed; }

/* Transform */
.scale-80 { transform: scale(0.8); }
.origin-top { transform-origin: top; }

/* Modal styles */
.modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-content {
    background: rgba(17, 24, 39, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 24px;
    max-width: 500px;
    width: 90%;
    backdrop-filter: blur(10px);
}

/* Button styles */
button {
    cursor: pointer;
    border: none;
    outline: none;
    transition: all 0.2s ease;
}

button:disabled {
    cursor: not-allowed;
    opacity: 0.5;
}

/* Asset card styles */
.asset-card {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 16px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.asset-display {
    display: flex;
    align-items: center;
    gap: 12px;
}

.asset-image {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    object-fit: cover;
}

.asset-symbol {
    font-size: 1.5rem;
    font-weight: 700;
    color: white;
}

/* Amount and term button styles */
.amount-button, .term-button {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 12px 16px;
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: center;
}

.amount-button.selected, .term-button.selected {
    background: rgba(59, 130, 246, 0.2);
    border-color: rgba(59, 130, 246, 0.5);
}

.amount-button:hover, .term-button:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
}

.term-button:not(.active) {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Responsive */
@media (max-width: 768px) {
    .max-w-2xl {
        max-width: 100%;
        padding: 0 16px;
    }
    
    .modal-content {
        width: 95%;
        padding: 16px;
    }
    
    .text-2xl {
        font-size: 1.25rem;
    }
    
    .grid-cols-3 {
        grid-template-columns: repeat(2, 1fr);
    }
}`;

  const trinityBondJs = `// Configuration from template variables
const CONFIG = {
    PRODUCT_ADDRESS: '${bondAddress}',
    PRODUCT_NAME: '${bondName}',
    CHAIN_ID: '369',
    RPC_URL: 'https://rpc.pulsechain.com',
    TRINITY_MASTER: '${ADDRESSES.TRINITY_MASTER}',
    TRINITY_TOKEN: '${ADDRESSES.TRINITY_TOKEN}',
    SUPPORTED_ASSETS: ${JSON.stringify(supportedAssets)},
    TOKEN_NAMES: ${JSON.stringify(TOKEN_NAMES)},
    TOKEN_SYMBOLS: ${JSON.stringify(TOKEN_SYMBOLS)},
    TRINITY_BOND_ABI: [
        {
          "inputs": [],
          "name": "name",
          "outputs": [{ "name": "", "type": "string" }],
          "stateMutability": "view",
          "type": "function"
        },
        {
          "inputs": [],
          "name": "description",
          "outputs": [{ "name": "", "type": "string" }],
          "stateMutability": "view",
          "type": "function"
        },
        {
          "inputs": [],
          "name": "getTerms",
          "outputs": [{
            "name": "",
            "type": "tuple[]",
            "components": [
              { "name": "returnPercentage", "type": "uint256" },
              { "name": "duration", "type": "uint256" },
              { "name": "active", "type": "bool" }
            ]
          }],
          "stateMutability": "view",
          "type": "function"
        },
        {
          "inputs": [],
          "name": "getSupportedAssets",
          "outputs": [{ "name": "", "type": "address[]" }],
          "stateMutability": "view",
          "type": "function"
        },
        {
          "inputs": [],
          "name": "trinityFee",
          "outputs": [{ "name": "", "type": "uint256" }],
          "stateMutability": "view",
          "type": "function"
        },
        {
          "inputs": [
            { "name": "asset", "type": "address" },
            { "name": "amount", "type": "uint256" },
            { "name": "termIndex", "type": "uint8" }
          ],
          "name": "createBond",
          "outputs": [],
          "stateMutability": "nonpayable",
          "type": "function"
        },
        {
          "inputs": [],
          "name": "properties",
          "outputs": [{ "name": "", "type": "address" }],
          "stateMutability": "view",
          "type": "function"
        },
        {
          "inputs": [],
          "name": "trinityEntity",
          "outputs": [{ "name": "", "type": "address" }],
          "stateMutability": "view",
          "type": "function"
        }
    ],
    TRINITY_PROPERTIES_ABI: [
        {
          "inputs": [
            { "name": "asset", "type": "address" },
            { "name": "key", "type": "string" }
          ],
          "name": "getProperty",
          "outputs": [{
            "name": "",
            "type": "tuple",
            "components": [
              { "name": "key", "type": "string" },
              { "name": "valueType", "type": "uint8" },
              { "name": "stringValue", "type": "string" },
              { "name": "uintValue", "type": "uint256" },
              { "name": "boolValue", "type": "bool" },
              { "name": "bytesValue", "type": "bytes" }
            ]
          }],
          "stateMutability": "view",
          "type": "function"
        }
    ],
    TRINITY_MASTER_ABI: [
        {
          "inputs": [],
          "name": "trinityMasterServiceFee",
          "outputs": [{ "name": "", "type": "uint32" }],
          "stateMutability": "view",
          "type": "function"
        }
    ],
    ERC20_ABI: [
        {
          "inputs": [{ "name": "account", "type": "address" }],
          "name": "balanceOf",
          "outputs": [{ "name": "", "type": "uint256" }],
          "stateMutability": "view",
          "type": "function"
        },
        {
          "inputs": [
            { "name": "owner", "type": "address" },
            { "name": "spender", "type": "address" }
          ],
          "name": "allowance",
          "outputs": [{ "name": "", "type": "uint256" }],
          "stateMutability": "view",
          "type": "function"
        },
        {
          "inputs": [
            { "name": "spender", "type": "address" },
            { "name": "amount", "type": "uint256" }
          ],
          "name": "approve",
          "outputs": [{ "name": "", "type": "bool" }],
          "stateMutability": "nonpayable",
          "type": "function"
        }
    ]
};

// Asset image mapping
const ASSET_IMAGES = {
    '${ADDRESSES.FED}': 'lib/fed.png',
    '${ADDRESSES.PDAI}': 'lib/pdai.png',
    '${ADDRESSES.TBILL}': 'lib/tbill.png'
};

// TrinityBond Application Class
class TrinityBondApp {
    constructor() {
        this.config = CONFIG;
        this.web3 = null;
        this.userAccount = null;
        this.isConnected = false;
        this.bondContract = null;
        this.propertiesContract = null;
        this.trinityMasterContract = null;
        this.selectedAsset = CONFIG.SUPPORTED_ASSETS[0];
        this.selectedAmount = '';
        this.selectedTermIndex = 0;
        this.terms = [];
        this.balances = {};
        this.allowances = {};
        this.fees = {
            purchaseFee: 0,
            serviceFee: 0,
            redemptionFee: 0,
            reclaimFee: 0
        };
        
        this.elements = {
            assetCard: document.getElementById('assetCard'),
            amountButtons: document.getElementById('amountButtons'),
            termButtons: document.getElementById('termButtons'),
            balanceInfo: document.getElementById('balanceInfo'),
            userBalance: document.getElementById('userBalance'),
            contractBalance: document.getElementById('contractBalance'),
            errorDisplay: document.getElementById('errorDisplay'),
            errorMessage: document.getElementById('errorMessage'),
            approveButton: document.getElementById('approveButton'),
            createBondButton: document.getElementById('createBondButton')
        };
        
        this.init();
    }

    async init() {
        try {
            await this.setupWeb3();
            await this.loadTerms();
            await this.loadProductInfo();
            await this.loadFees();
            await this.loadBalances();
            this.renderAssetCard();
            this.renderAmountButtons();
            this.renderTermButtons();
            this.updateBalanceDisplay();
            this.updateFees();
            this.updateButtons();
        } catch (error) {
            console.error('Initialization error:', error);
            this.showError('Failed to initialize application');
        }
    }

    async setupWeb3() {
        if (typeof window.ethereum !== 'undefined') {
            this.web3 = new Web3(window.ethereum);
            try {
                const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
                this.userAccount = accounts[0];
                this.isConnected = true;
                console.log('Connected to MetaMask');
            } catch (error) {
                console.error('User rejected connection');
            }
        } else {
            console.error('MetaMask not found');
        }
    }

    async loadTerms() {
        try {
            this.bondContract = new this.web3.eth.Contract(CONFIG.TRINITY_BOND_ABI, CONFIG.PRODUCT_ADDRESS);
            const terms = await this.bondContract.methods.getTerms().call();
            this.terms = terms.filter(term => term.active);
            console.log('Loaded terms:', this.terms);
        } catch (error) {
            console.error('Error loading terms:', error);
        }
    }

    async loadProductInfo() {
        try {
            const name = await this.bondContract.methods.name().call();
            const description = await this.bondContract.methods.description().call();
            document.getElementById('productName').textContent = name;
            document.getElementById('productDescription').textContent = description;
        } catch (error) {
            console.error('Error loading product info:', error);
        }
    }

    async loadFees() {
        try {
            const trinityFee = await this.bondContract.methods.trinityFee().call();
            this.fees.purchaseFee = this.web3.utils.fromWei(trinityFee, 'ether');
            
            // Load service fee from Trinity Master
            this.trinityMasterContract = new this.web3.eth.Contract(CONFIG.TRINITY_MASTER_ABI, CONFIG.TRINITY_MASTER);
            const serviceFee = await this.trinityMasterContract.methods.trinityMasterServiceFee().call();
            this.fees.serviceFee = serviceFee / 10000; // Convert from basis points
            
            console.log('Loaded fees:', this.fees);
        } catch (error) {
            console.error('Error loading fees:', error);
        }
    }

    async loadBalances() {
        if (!this.isConnected) return;
        
        try {
            for (const asset of CONFIG.SUPPORTED_ASSETS) {
                const tokenContract = new this.web3.eth.Contract(CONFIG.ERC20_ABI, asset);
                const balance = await tokenContract.methods.balanceOf(this.userAccount).call();
                this.balances[asset] = this.web3.utils.fromWei(balance, 'ether');
                
                const allowance = await tokenContract.methods.allowance(this.userAccount, CONFIG.PRODUCT_ADDRESS).call();
                this.allowances[asset] = this.web3.utils.fromWei(allowance, 'ether');
            }
            console.log('Loaded balances:', this.balances);
        } catch (error) {
            console.error('Error loading balances:', error);
        }
    }

    renderAssetCard() {
        if (!this.elements.assetCard) return;
        
        this.elements.assetCard.innerHTML = CONFIG.SUPPORTED_ASSETS.map(asset => \`
            <div class="asset-card" onclick="trinityBondApp.selectAsset('\${asset}')">
                <div class="asset-display">
                    <img src="\${ASSET_IMAGES[asset] || 'lib/fed.png'}" alt="Asset" class="asset-image">
                    <div>
                        <div class="asset-symbol">\${CONFIG.TOKEN_SYMBOLS[asset] || 'Unknown'}</div>
                        <div class="text-sm text-gray-400">\${CONFIG.TOKEN_NAMES[asset] || 'Unknown Token'}</div>
                    </div>
                </div>
            </div>
        \`).join('');
    }

    renderAmountButtons() {
        if (!this.elements.amountButtons) return;
        
        const amounts = ['100', '500', '1000', '5000'];
        this.elements.amountButtons.innerHTML = amounts.map(amount => \`
            <button class="amount-button" onclick="trinityBondApp.selectAmount('\${amount}')">
                \${amount}
            </button>
        \`).join('');
    }

    renderTermButtons() {
        if (!this.elements.termButtons) return;
        
        this.elements.termButtons.innerHTML = this.terms.map((term, index) => \`
            <button class="term-button \${term.active ? 'active' : ''}" onclick="trinityBondApp.selectTerm(\${index})">
                <div class="term-return">\${term.returnPercentage / 100}%</div>
                <div class="term-duration">\${term.duration} days</div>
            </button>
        \`).join('');
    }

    updateBalanceDisplay() {
        if (!this.elements.balanceInfo || !this.elements.userBalance) return;
        
        const balance = this.balances[this.selectedAsset] || '0';
        const symbol = CONFIG.TOKEN_SYMBOLS[this.selectedAsset] || '';
        
        this.elements.userBalance.textContent = \`Balance: \${balance} \${symbol}\`;
        this.elements.balanceInfo.style.display = 'block';
    }

    updateFees() {
        document.getElementById('purchaseFee').textContent = \`\${this.fees.purchaseFee} TRNTY\`;
        document.getElementById('serviceFee').textContent = \`\${(this.fees.serviceFee * 100).toFixed(2)}%\`;
        document.getElementById('redemptionFee').textContent = '0%';
        document.getElementById('reclaimFee').textContent = '0%';
    }

    updateButtons() {
        if (!this.elements.approveButton || !this.elements.createBondButton) return;
        
        const selectedAmount = parseFloat(this.selectedAmount);
        const balance = parseFloat(this.balances[this.selectedAsset] || '0');
        const allowance = parseFloat(this.allowances[this.selectedAsset] || '0');
        
        if (selectedAmount > allowance) {
            this.elements.approveButton.style.display = 'block';
            this.elements.createBondButton.style.display = 'none';
        } else {
            this.elements.approveButton.style.display = 'none';
            this.elements.createBondButton.style.display = 'block';
        }
    }

    selectAsset(asset) {
        this.selectedAsset = asset;
        this.updateBalanceDisplay();
        this.updateButtons();
    }

    selectAmount(amount) {
        this.selectedAmount = amount;
        this.updateButtons();
    }

    selectTerm(index) {
        this.selectedTermIndex = index;
    }

    async handleApproveToken() {
        if (!this.isConnected || !this.selectedAmount) return;
        
        try {
            const tokenContract = new this.web3.eth.Contract(CONFIG.ERC20_ABI, this.selectedAsset);
            const amount = this.web3.utils.toWei(this.selectedAmount, 'ether');
            
            await tokenContract.methods.approve(CONFIG.PRODUCT_ADDRESS, amount).send({
                from: this.userAccount
            });
            
            await this.loadBalances();
            this.updateButtons();
            this.hideError();
        } catch (error) {
            console.error('Approval error:', error);
            this.showError('Failed to approve token');
        }
    }

    async handleSubmit(event) {
        event.preventDefault();
        if (!this.isConnected || !this.selectedAmount) return;
        
        try {
            const amount = this.web3.utils.toWei(this.selectedAmount, 'ether');
            
            await this.bondContract.methods.createBond(
                this.selectedAsset,
                amount,
                this.selectedTermIndex
            ).send({
                from: this.userAccount
            });
            
            this.showSuccessModal();
            this.hideError();
        } catch (error) {
            console.error('Bond creation error:', error);
            this.showError('Failed to create bond');
        }
    }

    showError(message) {
        if (this.elements.errorDisplay && this.elements.errorMessage) {
            this.elements.errorMessage.textContent = message;
            this.elements.errorDisplay.style.display = 'block';
        }
    }

    hideError() {
        if (this.elements.errorDisplay) {
            this.elements.errorDisplay.style.display = 'none';
        }
    }

    showInfoModal() {
        document.getElementById('infoModal').style.display = 'flex';
    }

    hideInfoModal() {
        document.getElementById('infoModal').style.display = 'none';
    }

    showSuccessModal() {
        const successAsset = CONFIG.TOKEN_SYMBOLS[this.selectedAsset] || 'Unknown';
        const selectedTerm = this.terms[this.selectedTermIndex];
        const returnAmount = parseFloat(this.selectedAmount) * (1 + selectedTerm.returnPercentage / 10000);
        
        document.getElementById('successAsset').textContent = \`\${this.selectedAmount} \${successAsset}\`;
        document.getElementById('successTerm').textContent = \`Term: \${selectedTerm.duration} days\`;
        document.getElementById('successReturn').textContent = \`Return: \${selectedTerm.returnPercentage / 100}%\`;
        document.getElementById('successPayout').textContent = \`Payout: \${returnAmount.toFixed(2)} \${successAsset}\`;
        document.getElementById('successTrinityFee').textContent = \`Trinity Fee: \${this.fees.purchaseFee} TRNTY\`;
        document.getElementById('successServiceFee').textContent = \`Service Fee: \${(this.fees.serviceFee * 100).toFixed(2)}%\`;
        document.getElementById('successRedemptionFee').textContent = 'Redemption Fee: 0%';
        document.getElementById('successReclaimFee').textContent = 'Reclaim Fee: 0%';
        
        document.getElementById('successModal').style.display = 'flex';
    }

    hideSuccessModal() {
        document.getElementById('successModal').style.display = 'none';
    }
}

// Initialize the application
const trinityBondApp = new TrinityBondApp();`;

  // Web3 library (minified version)
  const web3Lib = `// Web3.js minified library would be included here
// For brevity, this is a placeholder
console.log('Web3 library loaded');`;

  // Framer Motion library (minified version)
  const framerMotionLib = `// Framer Motion minified library would be included here
// For brevity, this is a placeholder
console.log('Framer Motion library loaded');`;

  return {
    'index.html': trinityBondHtml,
    'trinitybond.css': trinityBondCss,
    'trinitybond.js': trinityBondJs,
    'lib/web3.min.js': web3Lib,
    'lib/framer-motion.min.js': framerMotionLib
  };
} 